import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  afterRender,
  computed,
  contentChild,
  effect,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideX } from '@ng-icons/lucide';
import { TranslocoModule } from '@ngneat/transloco';
import { ZardSelectItemComponent } from '../../../../app/shared/components/select/select-item.component';
import { ZardSelectComponent } from '../../../../app/shared/components/select/select.component';
import { SvzValidationMessageComponent } from '../../../svz-validation-message/svz-validation-message.component';
import { SVZ_LEGACY_ICON_PROVIDERS, resolveLegacyIconName } from '../../../zard-adapters/legacy-zard-icon';
import { SvzControl } from '../../svz-control';
import { ISvzChipOption } from '../interfaces/svz-chip.interface';
import { SVZ_OPTION_ROW_CLASSES, SvzSearchIconPosition, svzSearchBoxClasses, svzSearchIconClasses } from '../../svz-option-row.classes';
import { provideSolvaceIcons } from '../../../svz-icons/solvace-icons';
import { SvzChipAvatarComponent, SvzChipAvatarKind } from '../svz-chip-avatar.component';
import { ZardTooltipDirective } from '../../../../app/shared/components/tooltip/tooltip';
import { SvzTruncatedTooltipDirective } from '../../../svz-truncated-tooltip/svz-truncated-tooltip.directive';
import { svzDroplistListHeight } from '../../svz-droplist-list-height';
import { SvzChipListNavigator } from '../svz-chip-list-navigator';
import { svzChipNextValues } from '../svz-chip-selection';
import { SvzChipTriggerFitter } from '../svz-chip-trigger-fit';

interface InternalChipOption extends ISvzChipOption {
  key: string;
}

export type SvzChipOptionAvatar = 'none' | SvzChipAvatarKind;
export type SvzChipSelectionIndicator = 'checkbox' | 'check';
export type SvzChipSearchMode = 'local' | 'remote';

/** Prototype `.sd-opt` with a 24px avatar: padding 9 + 24 + 9. */
const AVATAR_ROW_HEIGHT = 42;

@Component({
  selector: 'svz-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-chip.component.html',
  // Prototype .sd-ck is primary; the Zard row variant paints every svg without a text-* class in muted-foreground and the
  // svg that ng-icon renders cannot carry one, so the wrapper colours it from the icon host (more specific than the row rule).
  // (::ng-deep: the svg is rendered by ng-icon and never gets this component's encapsulation attribute)
  styles: [
    "ng-icon[name='lucideCircleCheck'].svz-chip-check ::ng-deep svg { color: inherit !important; width: 18px !important; height: 18px !important; }",
  ],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SvzChipComponent), multi: true }, provideSolvaceIcons()],
  viewProviders: [...SVZ_LEGACY_ICON_PROVIDERS, provideIcons({ lucideCircleCheck, lucideX })],
  standalone: true,
  imports: [
    NgIcon,
    ZardSelectComponent,
    ZardSelectItemComponent,
    NgTemplateOutlet,
    SvzValidationMessageComponent,
    TranslocoModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    SvzChipAvatarComponent,
    ZardTooltipDirective,
    SvzTruncatedTooltipDirective,
  ],
})
export class SvzChipComponent extends SvzControl implements OnDestroy {
  readonly label = input<
    | string
    | {
        text: string;
        icon: string;
      }
    | null
  >(null);

  readonly icon = input('');

  readonly readonly = input<boolean>(false);

  readonly showValidation = input<boolean>(true);

  readonly readonlyStyle = input<'default' | 'chip' | 'transparent-chip'>('default');

  readonly options = input<ISvzChipOption[] | null | undefined>(undefined);

  private readonly syncOptions = effect(
    () => {
      this._options = (this.options() ?? []).map((option, index) => ({
        ...option,
        key: `chip-${index}`,
      }));

      this.filterOptions(this.searchText);
      // Plain fields feed the option list of an OnPush view: without this the options that arrive after the
      // first render are only projected on the next unrelated change detection.
      this.cdr.markForCheck();
      this.selectionVersion.update((version) => version + 1);
    },
    { allowSignalWrites: true }
  );

  private _options: InternalChipOption[] = [];

  readonly type = input<'single' | 'multiple'>('multiple');

  readonly appearance = input<'default' | 'primary'>('default');

  readonly inputId = input('');

  /**
   * Additive. `design-system` draws the prototypes' filter field (`.lbl-field` 13px/400 `#3a3a3a` 6px above the control,
   * `.sd-trg` 44px radius 8 with the primary border when open, `#9aa0ac` placeholder, `#8a90a6` chevron); `default`
   * keeps today's rendering for every existing consumer.
   */
  readonly fieldAppearance = input<'default' | 'design-system'>('default');
  readonly searchIconPosition = input<SvzSearchIconPosition>('start');

  readonly placeholder = input('');

  readonly fallbackTextValue = input('');

  readonly truncateTextValue = input<boolean>(false);

  readonly collapsedDisplaying = input<boolean>(true);

  readonly loading = input<boolean>(false);

  /** Fixed row height of the virtualized option list (px); rows with an avatar use at least 42. */
  readonly virtualItemSize = input<number>(36);

  /** Rows visible before the list scrolls; the viewport height is `min(options, rows) × row height`. */
  readonly virtualMaxRows = input<number>(8);

  /** Marker in front of every option and inside the trigger chips: person initials/picture or the area pin. */
  readonly optionAvatar = input<SvzChipOptionAvatar>('none');

  /** `checkbox` = Zard `z-select-item` checkbox; `check` = prototype `.sd-ck` circle-check on the selected row only. */
  readonly selectionIndicator = input<SvzChipSelectionIndicator>('checkbox');

  /** pt-BR key of the search box placeholder (defaults to `Pesquisar`). */
  readonly searchPlaceholder = input<string>('');

  readonly searchMinOptions = input<number>(10);

  readonly searchMode = input<SvzChipSearchMode>('local');

  readonly searchChange = output<string>();

  readonly onSelectChanged = output<{
    option: ISvzChipOption;
    selected: boolean;
  }>();

  readonly templateRef = contentChild(TemplateRef);
  protected readonly optionTooltipEnabled = computed(() => !this.templateRef() && this.selectionIndicator() === 'check');

  private readonly zSelect = viewChild(ZardSelectComponent);
  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  private readonly renderedItems = viewChildren(ZardSelectItemComponent);
  private readonly triggerValue = viewChild<ElementRef<HTMLElement>>('triggerValue');

  /** Prototype `fit()`: after every render (and on resize) hide the chips that overflow the trigger line into `+N`. */
  private readonly triggerFitter = new SvzChipTriggerFitter();
  private readonly fitTriggerAfterRender = afterRender(() => this.triggerFitter.fit(this.triggerValue()?.nativeElement));

  /**
   * The option list is a `cdk-virtual-scroll-viewport` rendered only after the dropdown opened once: `z-select-item`s
   * are content children of `z-select`, so a plain `@for` instantiated every option (a 500-user list × a few chips =
   * thousands of components) while the dropdown was still closed. Inside the viewport the items are no longer direct
   * content of `z-select`, so this wrapper wires them itself (`setSelectHost`), renders the trigger value through the
   * `[zSelectTrigger]` slot and owns the keyboard navigation over the full list.
   */
  protected readonly optionsRevealed = signal(false);

  /** Bumped on every value change so the rows' `isSelected` computed (fed through `selectedValue`) re-evaluates. */
  private readonly selectionVersion = signal(0);

  protected readonly selectedKeySet = computed(() => {
    this.selectionVersion();
    return new Set(this.selectedOptionKeys);
  });

  private readonly revealOptionsOnOpen = effect(
    () => {
      if (!this.zSelect()?.isOpen()) {
        return;
      }
      this.optionsRevealed.set(true);
      // The viewport lives inside the select's overlay template: it measures 0 while detached, so re-measure once
      // the portal is attached, then land on the first selected option.
      setTimeout(() => {
        this.viewport()?.checkViewportSize();
        this.navigator.focusInitial();
      });
    },
    { allowSignalWrites: true }
  );

  private readonly wiredItems = new WeakSet<object>();

  // `setSelectHost` writes the item's `select` signal, hence `allowSignalWrites`; without it the effect throws NG0600
  // on every run and the rows stay inert (click does nothing).
  private readonly wireRenderedItems = effect(
    () => {
      for (const item of this.renderedItems()) {
        this.wireItem(item);
      }
    },
    { allowSignalWrites: true }
  );

  /** Plays the `z-select` host role for a row the viewport rendered; each recycled row instance is wired once. */
  private wireItem(item: Pick<ZardSelectItemComponent, 'setSelectHost' | 'zValue'>): void {
    if (this.wiredItems.has(item)) {
      return;
    }
    this.wiredItems.add(item);
    item.setSelectHost({
      selectedValue: () => {
        this.selectionVersion();
        return this.selectedOptionKeys;
      },
      selectItem: (key: string) => this.selectByKey(key),
      navigateTo: () => this.navigator.setActiveByKey(item.zValue()),
    });
  }

  protected readonly navigator = new SvzChipListNavigator<InternalChipOption>({
    rows: (): readonly InternalChipOption[] => this.filteredOptions,
    keyOf: (option): string => option.key,
    isSelectable: (): boolean => true,
    selectedKeys: (): readonly string[] => this.selectedOptionKeys,
    viewport: (): CdkVirtualScrollViewport | undefined => this.viewport(),
    select: (option): void => this.selected(option),
  });

  protected searchText = '';
  protected filteredOptions: InternalChipOption[] = [];

  protected readonly trackByKey = (_index: number, option: InternalChipOption): string => option.key;

  /** The 24px badges (user, area) grow the row; the 16px colour swatch fits the plain `.sd-opt` row. */
  protected get rowHeight(): number {
    return this.hasTallAvatar ? Math.max(this.virtualItemSize(), AVATAR_ROW_HEIGHT) : this.virtualItemSize();
  }

  protected get avatarKind(): SvzChipAvatarKind {
    const avatar = this.optionAvatar();
    return avatar === 'none' ? 'user' : avatar;
  }

  /** Prototype `.sd-chip` repeats the directory badge on the trigger but never the colour swatch. */
  protected get showTriggerAvatar(): boolean {
    return this.hasTallAvatar;
  }

  private get hasTallAvatar(): boolean {
    return this.optionAvatar() === 'user' || this.optionAvatar() === 'area';
  }

  protected readonly searchBoxClasses = computed(() => svzSearchBoxClasses(this.searchIconPosition()));
  protected readonly searchIconClasses = computed(() => svzSearchIconClasses(this.searchIconPosition()));

  /** Prototype `.sd-pop-dir` (340px, people / area directories) vs the plain `.sd-pop` (240px). */
  protected get panelAppearance(): 'ds' | 'ds-directory' {
    return this.hasTallAvatar ? 'ds-directory' : 'ds';
  }

  protected get listHeight(): number {
    const rowsHeight = Math.min(this.filteredOptions.length, this.virtualMaxRows()) * this.rowHeight;
    return svzDroplistListHeight(rowsHeight, this.panelAppearance, this.showSearchInput);
  }

  protected get isDesignSystemField(): boolean {
    return this.fieldAppearance() === 'design-system';
  }

  protected get wrapperClasses(): string {
    return this.isDesignSystemField ? 'flex w-full flex-col gap-1.5' : 'flex w-full flex-col gap-2';
  }

  /** AB#76241 the empty message is still a flex item, so the design-system column added a second 6px gap under every field. */
  protected get validationClasses(): string {
    return this.isDesignSystemField ? 'empty:hidden' : '';
  }

  protected get labelClasses(): string {
    return this.isDesignSystemField ? 'inline-flex items-center gap-1 text-[13px] font-normal text-[#3a3a3a]' : 'inline-flex items-center gap-1 text-sm font-medium text-default';
  }

  /** Prototype `.sd-trg`: 44px, `padding:5px 10px`, `.sd.open .sd-trg{ border-color:primary }`, no focus halo, grey chevron, `.sd-disabled .sd-trg` grey. */
  protected get selectHostClasses(): string {
    return this.isDesignSystemField
      ? 'w-full [&_button]:py-[5px] [&_button]:px-2.5 [&[data-state=open]_button]:border-primary [&_button]:focus-visible:ring-0 [&_button]:focus-visible:border-primary [&_button:disabled]:bg-[#f4f5f7] [&_svg]:text-[#8a90a6] [&_svg]:opacity-100'
      : 'w-full';
  }

  protected get placeholderClasses(): string {
    return this.isDesignSystemField ? 'truncate text-[#9aa0ac]' : 'text-muted-foreground truncate';
  }

  protected get triggerChipClasses(): string {
    return this.isDesignSystemField
      ? 'inline-flex shrink-0 items-center gap-1.5 rounded-[8px] bg-[#eef0f4] py-1 pr-2 text-[13px] text-[#0d122f]'
      : 'inline-flex shrink-0 items-center gap-1.5 rounded-[8px] bg-[#eef0f4] py-0.5 pr-2 text-[13px] text-[#0d122f]';
  }

  protected get useCheckbox(): boolean {
    return this.type() === 'multiple' && this.selectionIndicator() === 'checkbox';
  }

  /** With the circle-check the row draws its own marker, so the Zard check that `z-select-item` renders is hidden.
   *  `pr-2.5` replaces the Zard `pr-8` marker slot with the `.sd-opt` 10px padding, since the marker sits inline. */
  protected get rowClass(): string {
    const baseClass = `mb-0 w-full pr-2.5 ${SVZ_OPTION_ROW_CLASSES}`;
    return this.selectionIndicator() === 'check' ? `${baseClass} [&>span:not(.truncate)]:hidden` : baseClass;
  }

  protected get canRemoveFromTrigger(): boolean {
    return !this.disabled() && !this.readonly() && !this.loading();
  }

  protected get showSearchInput(): boolean {
    return this.searchMode() === 'remote' || this._options.length > this.searchMinOptions();
  }

  protected get labelText(): string {
    const label = this.label();
    return typeof label === 'string' ? label : (label?.text ?? '');
  }

  protected get resolvedLabelIcon(): string | null {
    const label = this.label();
    const icon = typeof label === 'string' ? '' : label?.icon;
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get resolvedPrefixIcon(): string | null {
    const icon = this.icon();
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get selectedOptions(): InternalChipOption[] {
    const currentValues = this.normalizeValue(this.control.value);
    return this._options.filter((option) => currentValues.some((value) => value === option.value));
  }

  protected get selectedOptionKeys(): string[] {
    return this.selectedOptions.map((option) => option.key);
  }

  protected get visibleReadonlyOptions(): InternalChipOption[] {
    if (!this.collapsedDisplaying()) {
      return this.selectedOptions;
    }

    return this.selectedOptions.slice(0, 2);
  }

  protected get undisplayedSelectedOptions(): number {
    return Math.max(0, this.selectedOptions.length - this.visibleReadonlyOptions.length);
  }

  protected get selectedOptionsText(): string {
    if (!this.selectedOptions.length) return this.fallbackTextValue() || '';
    return this.selectedOptions.map((option) => this.renderOptionText(option)).join(', ');
  }

  filterOptions(value: string | null): void {
    this.searchText = value ?? '';
    this.navigator.reset();

    const normalizedFilter = this.searchText.trim().toLowerCase();
    if (!normalizedFilter || this.searchMode() === 'remote') {
      this.filteredOptions = [...this._options];
      return;
    }

    this.filteredOptions = this._options.filter((option) => String(option.label).toLowerCase().includes(normalizedFilter));
  }

  openOptions(): void {}

  closeOptions(options: { fromOnDestroy?: boolean } = {}): void {
    if (!options.fromOnDestroy) {
      this.onTouched();
    }
  }

  selected(option: ISvzChipOption): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const currentValues = this.normalizeValue(this.control.value);
    const isAlreadySelected = currentValues.includes(option.value);
    const nextValues = svzChipNextValues(this.type(), currentValues, option.value);

    const modelValue = this.type() === 'single' ? (nextValues[0] ?? null) : nextValues;

    this.control.setValue(modelValue, { emitEvent: false });
    this.value = modelValue;
    this.onChange(modelValue);
    this.onTouched();
    this.onSelectChanged.emit({ option, selected: !isAlreadySelected });
    this.cdr.markForCheck();
    this.selectionVersion.update((version) => version + 1);
  }

  /** `.sd-chip button`: removes the option from the trigger without opening the dropdown. */
  protected removeOption(option: ISvzChipOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.canRemoveFromTrigger) {
      this.selected(option);
    }
  }

  forceFocusItemWhenDownPressed(_event: unknown): void {}

  override ngOnDestroy(): void {
    this.triggerFitter.disconnect();
    super.ngOnDestroy();
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);
    this.filterOptions(this.searchText);
    this.selectionVersion.update((version) => version + 1);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filterOptions(value);
    this.searchChange.emit(value);
  }

  protected onListKeydown(event: KeyboardEvent): void {
    this.navigator.handleKeydown(event);
  }

  onZardSelectionChange(selection: string | string[]): void {
    const nextKeys = Array.isArray(selection) ? selection : [selection];
    const nextSelectedOptions = this._options.filter((option) => nextKeys.includes(option.key));
    const currentKeys = new Set(this.selectedOptionKeys);

    for (const option of this._options) {
      const willBeSelected = nextKeys.includes(option.key);
      const wasSelected = currentKeys.has(option.key);
      if (willBeSelected !== wasSelected) {
        this.onSelectChanged.emit({ option, selected: willBeSelected });
      }
    }

    const modelValue = this.type() === 'single' ? (nextSelectedOptions[0]?.value ?? null) : nextSelectedOptions.map((option) => option.value);

    this.control.setValue(modelValue, { emitEvent: false });
    this.value = modelValue;
    this.onChange(modelValue);
    this.onTouched();
    this.selectionVersion.update((version) => version + 1);
  }

  protected renderOptionText(option: ISvzChipOption | null | undefined): string {
    return String(option?.label ?? '');
  }

  /** An option may carry a picture URL in `data.picture`; otherwise the avatar falls back to the initials. */
  protected pictureOf(option: ISvzChipOption): string | null {
    const data = option.data as { picture?: string | null } | undefined;
    return data?.picture ?? null;
  }

  protected colorOf(option: ISvzChipOption): string | null {
    const data = option.data as { color?: string | null } | undefined;
    return data?.color ?? null;
  }

  private selectByKey(key: string): void {
    const option = this._options.find((entry) => entry.key === key);
    if (option) {
      this.selected(option);
    }
  }

  private normalizeValue(value: unknown): Array<string | number> {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    return Array.isArray(value) ? value : [value as string | number];
  }
}
